<script setup lang="ts">
const { activeSection } = useActiveSection()

const route = useRoute()
const { $tweakpane } = useNuxtApp()

const { frontPages } = useGlobalState()

const scrollProgress = computed(() => $tweakpane.PARAMS.scrollProgress)
const scrollDirection = computed<'up' | 'down'>(() =>
  $tweakpane.PARAMS.scrollDirection === -1 ? 'up' : 'down',
)
</script>

<template>
  <div
    class="lg:pr-bleed fixed right-0 bottom-0 z-1000 h-10 w-full lg:top-0 lg:bottom-auto lg:mt-3 lg:h-8 lg:w-1/2"
  >
    <nav class="lg:gap-gap flex h-full flex-row font-mono text-xs text-black uppercase select-none">
      <Transition name="frontpage-navigation-transition">
        <NuxtLink
          v-if="route.name !== 'page-slug'"
          :href="frontPages.index.slug"
          class="safari-blur before:ease-in-out-quint relative flex w-1/2 flex-nowrap items-center justify-between px-3 transition-all before:absolute before:inset-0 before:-z-1 before:border before:border-black/10 before:backdrop-blur-lg before:transition-transform before:duration-300"
          :class="{
            'before:bg-white-50 group duration-500 before:border-r-0 hover:px-6 hover:before:scale-x-[0.95] hover:before:-skew-x-[20deg]':
              route.path !== frontPages.index.slug,
            'group overflow-hidden text-(--link-text) duration-300 ease-in-out before:bg-(--link-bg)':
              route.path === frontPages.index.slug,
          }"
        >
          <div
            class="transition-bg progress absolute -inset-x-2 inset-y-px z-[1] block -skew-x-[20deg] transition-transform duration-300 ease-in-out before:absolute before:inset-0 before:z-[-1] before:bg-(--link-bg-progress) before:transition-transform"
            :style="{
              width: `${(route.path === frontPages.index.slug ? scrollProgress : 0) * 100}%`,
            }"
          />

          <span class="relative z-[2] max-w-full truncate overflow-hidden whitespace-nowrap">
            {{ frontPages.index.name }}
          </span>

          <Transition
            :name="`cycle-through-${scrollDirection}`"
            mode="out-in"
            class="cycle-wrapper"
          >
            <span
              v-if="
                route.path === frontPages.index.slug &&
                activeSection &&
                activeSection.chapterIndex > -1
              "
              :key="activeSection?.chapterIndex"
              class="relative z-[2] flex-shrink-0 pl-2"
            >
              [{{ zeroPad(activeSection?.chapterIndex + 1) }}]
            </span>
          </Transition>
        </NuxtLink>
      </Transition>

      <Transition name="frontpage-navigation-transition">
        <NuxtLink
          v-if="route.name !== 'page-slug'"
          :href="frontPages.getStarted.slug"
          class="safari-blur before:ease-in-out-quint before:safari-blur relative flex w-1/2 flex-nowrap items-center justify-between px-3 transition-all delay-100 before:absolute before:inset-0 before:-z-1 before:border before:border-black/10 before:backdrop-blur-lg before:transition-transform before:duration-300"
          :class="{
            'before:bg-white-50 group duration-500 before:border-l-0 hover:px-6 hover:before:scale-x-[0.97] hover:before:-skew-x-[20deg]':
              route.path !== frontPages.getStarted.slug,
            'group overflow-hidden text-(--link-text) duration-300 ease-in-out before:bg-(--link-bg)':
              route.path === frontPages.getStarted.slug,
          }"
        >
          <div
            class="transition-bg progress absolute -inset-x-2 inset-y-px z-[1] block -skew-x-[20deg] transition-transform duration-300 ease-in-out before:absolute before:inset-0 before:z-[-1] before:bg-(--link-bg-progress) before:transition-transform"
            :style="{
              width: `${(route.path === frontPages.getStarted.slug ? scrollProgress : 0) * 100}%`,
            }"
          />

          <span class="relative z-[2] max-w-full truncate overflow-hidden whitespace-nowrap">
            {{ frontPages.getStarted.name }}
          </span>

          <transition
            :name="`cycle-through-${scrollDirection}`"
            mode="out-in"
            class="cycle-wrapper"
          >
            <span
              v-if="
                route.path === frontPages.getStarted.slug &&
                activeSection &&
                activeSection.chapterIndex > -1
              "
              :key="activeSection?.chapterIndex"
              class="relative z-[2] flex-shrink-0 pl-2"
            >
              [{{ zeroPad(activeSection?.chapterIndex + 1) }}]
            </span>
          </transition>
        </NuxtLink>
      </Transition>
    </nav>
  </div>
</template>

<style lang="postcss">
.safari-blur:before {
  -webkit-backdrop-filter: blur(16px);
}

/* DOWN (default) */
.cycle-through-down-enter-active,
.cycle-through-down-leave-active {
  transition:
    transform 0.1s ease-in-out,
    opacity 0.1s ease-in-out;
}

.cycle-through-down-enter-from {
  transform: translateY(200%);
  opacity: 0;
}
.cycle-through-down-leave-to {
  transform: translateY(-200%);
  opacity: 0;
}

.cycle-through-down-enter-to,
.cycle-through-down-leave-from {
  transform: translateY(0%);
  opacity: 1;
}

/* UP */
.cycle-through-up-enter-active,
.cycle-through-up-leave-active {
  transition:
    transform 0.1s ease-in-out,
    opacity 0.1s ease-in-out;
}

.cycle-through-up-enter-from {
  transform: translateY(-200%);
  opacity: 0;
}
.cycle-through-up-leave-to {
  transform: translateY(200%);
  opacity: 0;
}

.cycle-through-up-enter-to,
.cycle-through-up-leave-from {
  transform: translateY(0%);
  opacity: 1;
}

.frontpage-navigation-transition-enter-active,
.frontpage-navigation-transition-leave-active {
  transition-property: clip-path;
  transition-duration: 600ms;
  transition-timing-function: var(--ease-in-out-quint);
}

.frontpage-navigation-transition-enter-from,
.frontpage-navigation-transition-leave-to {
  clip-path: polygon(-100% 0%, 0% 0%, -05% 100%, -100% 100%);
}

.frontpage-navigation-transition-enter-to,
.frontpage-navigation-transition-leave-from {
  clip-path: polygon(-100% 0%, 105% 0%, 100% 100%, -100% 100%);
}
</style>
